import React, { useEffect, useState } from 'react';
import { useOverlay } from '../../contexts/OverlayContext';
import { typography } from '../../styles/typography';
import { Card } from '../ui';
import AssignEmployeeZonesTable from './AssignEmployeeZonesTable';
import { employeeApi } from './employeeApi';

const ZoneAssignmentManager = ({ 
  siteId, 
  zoneGuards = {},
  employeeType = 'guards',
  onZoneGuardsChange,
  shiftValidationData = null
}) => {
  const [zones, setZones] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeZoneId, setActiveZoneId] = useState(null);
  
  // Get overlay functions from context
  const { openOverlay } = useOverlay();

  // Fetch zones when siteId changes
  useEffect(() => {
    if (siteId) {
      fetchZones();
    }
  }, [siteId]);

  const fetchZones = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const zonesData = await employeeApi.getZonesBySiteId(siteId);
      
      // Process zones data to match expected structure
      const processedZones = zonesData.map(zone => ({
        zone_id: zone.b_id || zone.zone_id,
        zone_name: zone.zone?.name || zone.zone_name || 'Unnamed Zone',
        description: zone.zone?.description || zone.description || '',
        site_id: siteId
      }));
      
      setZones(processedZones);
      
      // Set the first zone as active when zones are loaded
      if (processedZones.length > 0) {
        setActiveZoneId(processedZones[0].zone_id);
      }
    } catch (error) {
      console.error('Error fetching zones:', error);
      setError(error.message || 'Failed to fetch zones');
      setZones([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Zone management handlers for AssignEmployeeZonesTable
  const handleAddEmployeeToZone = () => {
    if (!activeZoneId) {
      console.warn('No active zone selected');
      return;
    }
    
    // Open AssignEmployeesToZones overlay
    openOverlay('assignEmployeesToZones', {
      siteId: siteId,
      employeeType: employeeType,
      initialZoneGuards: zoneGuards,
      activeZoneId: activeZoneId,
      shiftValidationData: shiftValidationData,
      onSave: handleSaveZoneAssignments
    });
  };

  const handleRemoveEmployeeFromZone = (index) => {
    // Remove employee from the active zone
    if (activeZoneId && zoneGuards[activeZoneId]) {
      const currentZoneGuards = zoneGuards[activeZoneId] || [];
      const updatedZoneGuards = {
        ...zoneGuards,
        [activeZoneId]: currentZoneGuards.filter((_, i) => i !== index)
      };
      onZoneGuardsChange(updatedZoneGuards);
    }
  };

  const handleDeleteEmployeeFromZone = (index) => {
    handleRemoveEmployeeFromZone(index);
  };

  // Handle zone note changes
  const handleZoneNoteChange = (updatedEmployee, newNote) => {
    // Update the guard in the active zone with the new note
    if (activeZoneId && zoneGuards[activeZoneId]) {
      const currentZoneGuards = zoneGuards[activeZoneId] || [];
      const updatedZoneGuards = {
        ...zoneGuards,
        [activeZoneId]: currentZoneGuards.map(guard => {
          const guardKey = typeof guard === 'string' ? guard : (guard.user_id || guard.employee_id);
          const updatedEmployeeKey = typeof updatedEmployee === 'string' ? updatedEmployee : (updatedEmployee.user_id || updatedEmployee.employee_id);
          
          if (guardKey === updatedEmployeeKey) {
            return {
              ...guard,
              notes: newNote,
              user_notes: newNote
            };
          }
          return guard;
        })
      };
      onZoneGuardsChange(updatedZoneGuards);
    }
  };

  // Handle saving zone assignments from the overlay
  const handleSaveZoneAssignments = (updatedZoneGuards) => {
    console.log('Saving zone assignments:', updatedZoneGuards);
    onZoneGuardsChange(updatedZoneGuards);
  };


  if (isLoading) {
    return (
      <div className="w-full">
        <div className="text-center text-gray-500 py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-2"></div>
          Loading zones...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full">
        <div className="text-center text-red-500 py-8">
          <div className="mb-2">⚠️</div>
          <div className="text-sm mb-4">{error}</div>
          <button 
            onClick={fetchZones}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className={typography.h3} style={{ color: '#000000' }}>
          Assign to zone
        </h3>
      </div>

      {/* Zones Tabs */}
      {zones.length > 0 ? (
        <div className="space-y-4">
          {/* Zone Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {zones.map((zone) => (
                <button
                  key={zone.zone_id}
                  onClick={() => setActiveZoneId(zone.zone_id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeZoneId === zone.zone_id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {zone.zone_name}
                  {zoneGuards[zone.zone_id] && zoneGuards[zone.zone_id].length > 0 && (
                    <span className="ml-2 bg-blue-100 text-blue-600 text-xs px-2 py-1 rounded-full">
                      {zoneGuards[zone.zone_id].length}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Active Zone Content */}
          {activeZoneId && (
            <div className="space-y-4">
              {/* Zone Header */}
              <div className="flex justify-between items-center">
                <h4 className={typography.h5} style={{ color: '#000000' }}>
                  {zones.find(z => z.zone_id === activeZoneId)?.zone_name} Zone
                </h4>
                <div className="text-sm text-gray-500">
                  {zoneGuards[activeZoneId]?.length || 0} employees assigned
                </div>
              </div>
              
              {/* Employee Assignment Table for Active Zone */}
              <AssignEmployeeZonesTable
                assignedEmployeesToZones={zoneGuards[activeZoneId] || []}
                onAddEmployeeToZone={handleAddEmployeeToZone}
                onRemoveEmployeeFromZone={handleRemoveEmployeeFromZone}
                onDeleteEmployeeFromZone={handleDeleteEmployeeFromZone}
                onNoteChange={handleZoneNoteChange}
              />
            </div>
          )}
        </div>
      ) : (
        <Card.Gradient className="p-8 text-center">
          <div className="text-gray-500">
            <div className="mb-4">
              <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h4 className={typography.h5} style={{ color: '#000000', marginBottom: '0.5rem' }}>
              No Zones Available
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              This site doesn't have any zones yet. Create a zone to start assigning {employeeType}.
            </p>
          </div>
        </Card.Gradient>
      )}
    </div>
  );
};

export default ZoneAssignmentManager;
